import React, { useMemo } from "react";

import { GoalBasedFinancialPlanSingleResponse } from "@fartherfinance/frontend/api/FinancialPlans/plan/types";

import { assertUnreachable } from "@src/utils/assertUnreachable";

import Assets from "./pages/Assets/Assets";
import Expenses from "./pages/Expenses/Expenses";
import Goals from "./pages/Goals/Goals";
import Income from "./pages/Income/Income";
import Insurance from "./pages/Insurance/Insurance";
import Overview from "./pages/Overview/Overview";
import PlanSummary from "./pages/PlanSummary/PlanSummary";
import Savings from "./pages/Savings/Savings";
import { ClientAndSpouseDetails } from "./pages/Shared/hooks/useGetClientAndSpouseDetails";
import { useCreateUpdateFinancialPlanContext } from "./reducer/Context";
interface Props {
  singleGoalBasedFinancialPlan: GoalBasedFinancialPlanSingleResponse;
  clientSpouseData: ClientAndSpouseDetails;
}

const FinancialPlansCreateUpdateFlowSteps: React.FC<Props> = ({
  singleGoalBasedFinancialPlan,
  clientSpouseData,
}) => {
  const {
    state: { pageIndex },
  } = useCreateUpdateFinancialPlanContext();

  const goalOwnerOptions = useMemo(() => {
    const { client, spouse } = clientSpouseData;

    return spouse === undefined
      ? [
          {
            label: client.fullName,
            value: client.clientId,
            dob: client.dob,
          },
        ]
      : [
          {
            label: client.fullName,
            value: client.clientId,
            dob: client.dob,
          },
          {
            label: spouse.fullName,
            value: spouse.clientId,
            dob: spouse.dob,
          },
        ];
  }, [clientSpouseData]);

  const otherOwnersOptions = useMemo(() => {
    const { client, spouse } = clientSpouseData;

    if (singleGoalBasedFinancialPlan.retirementHorizon === undefined) {
      return [];
    }

    const hasSpouseRetirementGoal =
      singleGoalBasedFinancialPlan.retirementHorizon.some(
        (rh) => rh.owner !== client.clientId
      );

    const noSpouseRetirementOrSpouse =
      spouse === undefined || !hasSpouseRetirementGoal;

    return noSpouseRetirementOrSpouse
      ? [
          {
            label: client.fullName,
            value: client.clientId,
            dob: client.dob,
          },
        ]
      : [
          {
            label: client.fullName,
            value: client.clientId,
            dob: client.dob,
          },
          {
            label: spouse.fullName,
            value: spouse.clientId,
            dob: spouse.dob,
          },
        ];
  }, [clientSpouseData, singleGoalBasedFinancialPlan.retirementHorizon]);

  switch (pageIndex) {
    case 0:
      return <Overview plan={singleGoalBasedFinancialPlan} />;
    case 1:
      return (
        <Goals
          ownerOptions={goalOwnerOptions}
          clientSpouseData={clientSpouseData}
          singleGoalBasedFinancialPlan={singleGoalBasedFinancialPlan}
        />
      );
    case 2:
      return <Assets clientSpouseData={clientSpouseData} />;
    case 3:
      return <Income clientSpouseData={clientSpouseData} />;
    case 4:
      return <Savings clientSpouseData={clientSpouseData} />;
    case 5:
      return (
        <Expenses
          ownerOptions={otherOwnersOptions}
          clientSpouseData={clientSpouseData}
          singleGoalBasedFinancialPlan={singleGoalBasedFinancialPlan}
        />
      );
    case 6:
      return (
        <Insurance
          singleGoalBasedFinancialPlan={singleGoalBasedFinancialPlan}
          clientSpouseData={clientSpouseData}
          ownerOptions={otherOwnersOptions}
        />
      );
    case 7:
      return (
        <PlanSummary
          singleGoalBasedFinancialPlan={singleGoalBasedFinancialPlan}
          clientSpouseData={clientSpouseData}
        />
      );
    default:
      return assertUnreachable(pageIndex);
  }
};

export default FinancialPlansCreateUpdateFlowSteps;
